import React, { useState, useMemo, useEffect } from 'react';
import { Search, X, Filter, ChevronDown } from 'lucide-react';
import { RankingParticipant, Team } from 'types/ranking';
import { useGetTeam } from 'hooks/team/useTeam';
import './styles.scss';

interface ParticipantsSelectorProps {
  participants: RankingParticipant[];
  selectedParticipantIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  loading?: boolean;
}

type SortOption = 'alphabetic-asc' | 'alphabetic-desc' | 'team';

const ParticipantsSelector: React.FC<ParticipantsSelectorProps> = ({
  participants,
  selectedParticipantIds,
  onSelectionChange,
  loading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('alphabetic-asc');
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const { getTeam } = useGetTeam();

  useEffect(() => {
    const fetchTeams = async () => {
      setLoadingTeams(true);
      try {
        const teamsData = await getTeam();
        if (Array.isArray(teamsData)) {
          setTeams(teamsData);
        }
      } catch (error) {
        console.error('Erro ao carregar times:', error);
      } finally {
        setLoadingTeams(false);
      }
    };

    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getParticipantName = (participant: RankingParticipant) => {
    return participant.name || participant.nome || participant.email || 'Sem nome';
  };

  const sortedParticipants = useMemo(() => {
    let sorted = [...participants];

    if (sortBy === 'alphabetic-asc' || sortBy === 'alphabetic-desc') {
      sorted.sort((a, b) => {
        const nameA = getParticipantName(a).toLowerCase();
        const nameB = getParticipantName(b).toLowerCase();
        return sortBy === 'alphabetic-asc' 
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      });
    }

    return sorted;
  }, [participants, sortBy]);

  const filteredParticipants = useMemo(() => {
    let filtered = sortedParticipants;

    if (selectedTeam !== 'all') {
      if (selectedTeam === 'no-team') {
        filtered = filtered.filter(p => 
          !p.teams || 
          p.teams.length === 0 || 
          (p.teams.length === 1 && !p.teams[0])
        );
      } else {
        filtered = filtered.filter(p => 
          p.teams && 
          p.teams.length > 0 && 
          p.teams.includes(selectedTeam)
        );
      }
    }

    if (!searchTerm.trim()) return filtered;

    const term = searchTerm.toLowerCase();
    return filtered.filter(
      (p) =>
        p.name?.toLowerCase().includes(term) ||
        p.nome?.toLowerCase().includes(term) ||
        p.email?.toLowerCase().includes(term)
    );
  }, [sortedParticipants, searchTerm, selectedTeam]);

  const toggleParticipant = (participantId: string) => {
    if (selectedParticipantIds.includes(participantId)) {
      onSelectionChange(selectedParticipantIds.filter((id) => id !== participantId));
    } else {
      onSelectionChange([...selectedParticipantIds, participantId]);
    }
  };

  const selectAll = () => {
    const allFilteredIds = filteredParticipants.map((p) => p._id);
    const uniqueIds = Array.from(new Set([...selectedParticipantIds, ...allFilteredIds]));
    onSelectionChange(uniqueIds);
  };

  const deselectAll = () => {
    onSelectionChange([]);
  };

  const getInitials = (name?: string) => {
    if (!name) return '??';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getTeamName = (teamId: string) => {
    const team = teams.find(t => t._id === teamId);
    return team?.name || teamId;
  };

  const getSortLabel = () => {
    switch (sortBy) {
      case 'alphabetic-asc':
        return 'A-Z';
      case 'alphabetic-desc':
        return 'Z-A';
      default:
        return 'Ordenar';
    }
  };

  return (
    <div className="participants-selector">
      <div className="selector-header">
        <div className="search-bar">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Buscar por nome ou e-mail..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              className="clear-search"
              onClick={() => setSearchTerm('')}
              type="button"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className="filter-controls">
          <div className="filter-group">
            <label className="filter-label">Time:</label>
            <select
              className="filter-select"
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              disabled={loadingTeams || loading}
            >
              <option value="all">Todos os times</option>
              <option value="no-team">Sem time</option>
              {teams.map((team) => (
                <option key={team._id} value={team._id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <button
              type="button"
              className="filter-button"
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              disabled={loading}
            >
              <Filter size={16} />
              <span>{getSortLabel()}</span>
              <ChevronDown size={16} className={showFilterDropdown ? 'rotated' : ''} />
            </button>

            {showFilterDropdown && (
              <div className="filter-dropdown">
                <button
                  type="button"
                  className={`dropdown-item ${sortBy === 'alphabetic-asc' ? 'active' : ''}`}
                  onClick={() => {
                    setSortBy('alphabetic-asc');
                    setShowFilterDropdown(false);
                  }}
                >
                  Alfabética (A-Z)
                </button>
                <button
                  type="button"
                  className={`dropdown-item ${sortBy === 'alphabetic-desc' ? 'active' : ''}`}
                  onClick={() => {
                    setSortBy('alphabetic-desc');
                    setShowFilterDropdown(false);
                  }}
                >
                  Alfabética (Z-A)
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="selection-actions">
          <button
            type="button"
            className="btn-select-action"
            onClick={selectAll}
            disabled={loading}
          >
            Selecionar todos
          </button>
          <button
            type="button"
            className="btn-select-action"
            onClick={deselectAll}
            disabled={loading || selectedParticipantIds.length === 0}
          >
            Limpar seleção
          </button>
        </div>
      </div>

      <div className="selection-summary">
        <span className="summary-text">
          {selectedParticipantIds.length} de {participants.length} participante(s) selecionado(s)
        </span>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Carregando participantes...</p>
        </div>
      ) : (
        <div className="participants-list">
          {filteredParticipants.length > 0 ? (
            filteredParticipants.map((participant) => {
              const isSelected = selectedParticipantIds.includes(participant._id);

              return (
                <div
                  key={participant._id}
                  className={`participant-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggleParticipant(participant._id)}
                >
                  <div className="participant-checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="checkbox-input"
                    />
                    <div className="checkbox-custom">
                      {isSelected && (
                        <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                          <path
                            d="M1 5L4.5 8.5L11 1.5"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>

                  <div className="participant-avatar">
                    {participant.fotoDePerfil || participant.avatar ? (
                      <img
                        src={participant.fotoDePerfil || participant.avatar}
                        alt={getParticipantName(participant)}
                      />
                    ) : (
                      <span className="avatar-initials">{getInitials(getParticipantName(participant))}</span>
                    )}
                  </div>

                  <div className="participant-info">
                    <span className="participant-name">{getParticipantName(participant)}</span>
                    {participant.email && (
                      <span className="participant-email">{participant.email}</span>
                    )}
                    {participant.teams && participant.teams.length > 0 && (
                      <span className="participant-team">
                        {getTeamName(participant.teams[0])}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="empty-state">
              <p>Nenhum participante encontrado</p>
              {searchTerm && (
                <button
                  className="btn-clear-search"
                  onClick={() => setSearchTerm('')}
                  type="button"
                >
                  Limpar busca
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ParticipantsSelector;
